class AcrHeroPlayer extends EventTarget {
  constructor(durationInSec) {
    super();
    this.duration = durationInSec * 1000;
    this.remaining = this.duration;
    this.state = "paused";
    this.timer = null;
    this.startTime = 0;
    this._mustResumeOnVisibility = false;
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  onVisibilityChange() {
    if (document.hidden) {
      if (this.state === "started") {
        this._mustResumeOnVisibility = true;
        this.pause();
        this.dispatchEvent(new CustomEvent("player:visibility-pause"));
      }
    } else if (this._mustResumeOnVisibility) {
      this._mustResumeOnVisibility = false;
      this.resume(false);
      this.dispatchEvent(new CustomEvent("player:visibility-resume"));
    }
  }

  pause() {
    if (this.state !== "started") return;
    clearTimeout(this.timer);
    this.state = "paused";
    this.remaining -= Date.now() - this.startTime;
    this.dispatchEvent(new CustomEvent("player:pause"));
  }

  resume(restart = false) {
    if (restart) {
      this.start();
      return;
    }
    if (this.state !== "paused") return;
    clearTimeout(this.timer);
    this.startTime = Date.now();
    this.state = "started";
    this.timer = setTimeout(() => this.dispatchEvent(new CustomEvent("player:end")), this.remaining);
    this.dispatchEvent(new CustomEvent("player:resume"));
  }

  start() {
    clearTimeout(this.timer);
    this.remaining = this.duration;
    this.startTime = Date.now();
    this.state = "started";
    this.timer = setTimeout(() => this.dispatchEvent(new CustomEvent("player:end")), this.remaining);
    this.dispatchEvent(new CustomEvent("player:start"));
  }

  stop() {
    clearTimeout(this.timer);
    this.state = "stopped";
    this.dispatchEvent(new CustomEvent("player:stop"));
  }

  destroy() {
    this.stop();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }
}

class AcrHeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._slides = [...this.querySelectorAll(".acr-two-column-image__hero-slide")];
    if (this._slides.length <= 1) return;

    this._isMobileCarousel = this.classList.contains("acr-two-column-image__hero-carousel--mobile");
    this._mediaQuery = this._createMobileMediaQuery();
    this._onMediaChange = this._onMediaChange.bind(this);
    this._mediaQuery.addEventListener("change", this._onMediaChange);

    this._index = Math.max(
      0,
      this._slides.findIndex((slide) => slide.classList.contains("is-selected"))
    );
    this._onResize = this._onResize.bind(this);
    window.addEventListener("resize", this._onResize);
    this._syncSlides(false);
    this._syncCarouselHeight(this._slides[this._index], { animate: false });

    const autoplaySeconds = parseFloat(this.getAttribute("autoplay") || "0");
    if (autoplaySeconds > 0) {
      this._player = new AcrHeroPlayer(autoplaySeconds);
      this._player.addEventListener("player:end", () => this.next());
      this._player.addEventListener("player:start", (event) => this.handleAutoplayProgress(event));
      this._player.addEventListener("player:pause", (event) => this.handleAutoplayProgress(event));
      this._player.addEventListener("player:resume", (event) => this.handleAutoplayProgress(event));
      this._player.addEventListener("player:stop", (event) => this.handleAutoplayProgress(event));
      this._player.addEventListener("player:visibility-pause", (event) => this.handleAutoplayProgress(event));
      this._player.addEventListener("player:visibility-resume", (event) => this.handleAutoplayProgress(event));
      this._syncActiveState();
    }

    this.addEventListener("carousel:change", () => this._preloadNearbyImages());
    this._setupSwipe();
    this._preloadNearbyImages();
  }

  disconnectedCallback() {
    this._mediaQuery?.removeEventListener("change", this._onMediaChange);
    window.removeEventListener("resize", this._onResize);
    this._player?.destroy();
    this._touchAbortController?.abort();
  }

  get selectedIndex() {
    return this._index;
  }

  get player() {
    return this._player;
  }

  previous({ instant = false } = {}) {
    const index = (this._index - 1 + this._slides.length) % this._slides.length;
    return this.select(index, { instant, direction: "previous" });
  }

  next({ instant = false } = {}) {
    const index = (this._index + 1) % this._slides.length;
    return this.select(index, { instant, direction: "next" });
  }

  select(index, { instant = false, direction = null } = {}) {
    if (!(index in this._slides) || index === this._index) {
      return Promise.resolve();
    }

    if (this._transitioning) {
      this.dispatchEvent(
        new CustomEvent("carousel:change", {
          detail: { index: this._index, cell: this._slides[this._index] },
        })
      );
      return this._transitionPromise ?? Promise.resolve();
    }

    const previousIndex = this._index;
    const fromSlide = this._slides[previousIndex];
    const toSlide = this._slides[index];
    direction ??= this._inferDirection(previousIndex, index);

    this._index = index;
    this._transitioning = true;

    this._player?.pause();
    this._syncCarouselHeight(toSlide, { animate: !instant });
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: toSlide } }));
    this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index, cell: toSlide } }));

    this._transitionPromise = this._animateTransition(fromSlide, toSlide, direction, instant)
      .then(() => {
        this._syncSlides(false);
        if (this._isActiveCarousel()) {
          this._player?.resume(true);
        }
        this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index, cell: toSlide } }));
      })
      .finally(() => {
        this._transitioning = false;
        this._transitionPromise = null;
      });

    return this._transitionPromise;
  }

  handleAutoplayProgress(event) {
    switch (event.type) {
      case "player:start":
        this.style.setProperty("--slideshow-progress-duration", `${this.getAttribute("autoplay")}s`);
        this.style.setProperty("--slideshow-progress-play-state", "running");
        break;
      case "player:resume":
        this.style.setProperty("--slideshow-progress-duration", `${this._player.remaining / 1000}s`);
        this.style.setProperty("--slideshow-progress-play-state", "running");
        break;
      case "player:pause":
      case "player:visibility-pause":
        this.style.setProperty("--slideshow-progress-play-state", "paused");
        break;
      case "player:stop":
        this.style.setProperty("--slideshow-progress-duration", "0s");
        this.style.setProperty("--slideshow-progress-play-state", "paused");
        break;
      case "player:visibility-resume":
        this.style.setProperty("--slideshow-progress-play-state", "running");
        break;
    }
  }

  _onResize() {
    this._syncCarouselHeight(this._slides[this._index], { animate: false });
  }

  _syncCarouselHeight(slide, { animate = true } = {}) {
    if (this._isMobileCarousel || !this._isActiveCarousel()) {
      this.style.height = "";
      return;
    }

    if (!slide) return;

    const height = slide.getBoundingClientRect().height;
    if (!height) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!animate || reducedMotion) {
      const previousTransition = this.style.transition;
      this.style.transition = "none";
      this.style.height = `${height}px`;
      this.offsetHeight;
      this.style.transition = previousTransition;
      return;
    }

    this.style.height = `${height}px`;
  }

  _inferDirection(fromIndex, toIndex) {
    const slideCount = this._slides.length;
    const forwardSteps = (toIndex - fromIndex + slideCount) % slideCount;
    const backwardSteps = (fromIndex - toIndex + slideCount) % slideCount;

    if (forwardSteps < backwardSteps) return "next";
    if (backwardSteps < forwardSteps) return "previous";
    return toIndex > fromIndex ? "next" : "previous";
  }

  _syncSlides(animate = false) {
    this._slides.forEach((slide, index) => {
      const isSelected = index === this._index;
      slide.classList.toggle("is-selected", isSelected);
      slide.style.visibility = isSelected ? "visible" : "hidden";
      slide.style.transform = "";
      slide.style.zIndex = isSelected ? "1" : "0";
      if (!animate) {
        slide.getAnimations().forEach((animation) => animation.cancel());
      }
    });
  }

  _animateTransition(fromSlide, toSlide, direction, instant) {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (instant || reducedMotion) {
      return Promise.resolve();
    }

    const enterOffset = direction === "next" ? "100%" : "-100%";
    const leaveOffset = direction === "next" ? "-100%" : "100%";

    fromSlide.style.visibility = "visible";
    toSlide.style.visibility = "visible";
    toSlide.style.zIndex = "2";
    fromSlide.style.zIndex = "1";
    toSlide.style.transform = `translateX(${enterOffset})`;

    const duration = 650;
    const easing = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

    const leaveAnimation = fromSlide.animate(
      [
        { transform: "translateX(0)" },
        { transform: `translateX(${leaveOffset})` },
      ],
      { duration, easing, fill: "forwards" }
    );

    const enterAnimation = toSlide.animate(
      [
        { transform: `translateX(${enterOffset})` },
        { transform: "translateX(0)" },
      ],
      { duration, easing, fill: "forwards" }
    );

    return Promise.all([leaveAnimation.finished, enterAnimation.finished]).catch(() => undefined);
  }

  _preloadNearbyImages() {
    if (!this._isActiveCarousel()) return;

    const indexes = [
      this._index,
      (this._index + 1) % this._slides.length,
      (this._index - 1 + this._slides.length) % this._slides.length,
    ];

    indexes.forEach((index) => {
      this._slides[index]?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.removeAttribute("loading");
      });
    });
  }

  _createMobileMediaQuery() {
    const parsed = Number.parseInt(this.dataset.mobileBreakpoint || "", 10);
    const maxWidth = Number.isFinite(parsed) && parsed > 0 ? parsed : 999;
    return window.matchMedia(`(max-width: ${maxWidth}px)`);
  }

  _isActiveCarousel() {
    const isMobileViewport = (this._mediaQuery ?? this._createMobileMediaQuery()).matches;
    return this._isMobileCarousel ? isMobileViewport : !isMobileViewport;
  }

  _syncActiveState() {
    if (!this._player) return;

    if (this._isActiveCarousel()) {
      if (this._player.state === "stopped" || this._player.state === "paused") {
        this._player.start();
      }
      return;
    }

    this._player.stop();
  }

  _onMediaChange() {
    this._syncActiveState();
    this._syncCarouselHeight(this._slides[this._index], { animate: false });
    if (this._isActiveCarousel()) {
      this._preloadNearbyImages();
    }
  }

  _setupSwipe() {
    this._touchAbortController = new AbortController();
    const options = { passive: true, signal: this._touchAbortController.signal };
    let startX = 0;
    let startY = 0;

    this.addEventListener(
      "touchstart",
      (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      options
    );

    this.addEventListener(
      "touchend",
      (event) => {
        const deltaX = event.changedTouches[0].clientX - startX;
        const deltaY = event.changedTouches[0].clientY - startY;

        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;

        if (deltaX < 0) {
          this.next();
        } else {
          this.previous();
        }
      },
      options
    );
  }
}

if (!window.customElements.get("acr-hero-carousel")) {
  window.customElements.define("acr-hero-carousel", AcrHeroCarousel);
}
